import { readSessionString } from '@/lib/sessionStorage'

export const BACK_URL_SESSION_KEY = 'authBackUrl'

export const BACK_URL_QUERY_KEY = 'BACK_URL'

export const SUCCESS_BACK_URL_QUERY_KEY = 'backUrl'

export function isAuthSpaUrl(url: string): boolean {
  return url.includes('auth_new') || /(?:^|\/)auth(?:\/|$|\?|#)/.test(url)
}

export function decodeBackUrlValue(raw: string): string {
  let url = raw.trim()
  if (url && /%[0-9A-Fa-f]{2}/.test(url)) {
    try {
      url = decodeURIComponent(url)
    } catch {}
  }
  return url
}

export function isUsableBackUrl(url: string | null | undefined): url is string {
  const value = (url || '').trim()
  if (!value.length || value === '/' || isAuthSpaUrl(value)) return false

  // Абсолютный URL корня сайта (https://domain/) — то же, что «/»: после логина
  // бесполезен, а в query без энкодинга ломает /auth/.
  try {
    if (/^https?:\/\//i.test(value) || value.startsWith('//')) {
      const parsed = new URL(value, 'https://placeholder.local')
      if (
        (parsed.pathname === '/' || parsed.pathname === '') &&
        !parsed.search &&
        !parsed.hash
      ) {
        return false
      }
    }
  } catch {
    return false
  }

  return true
}

/** REF_CODE из URL возврата (корзина и т.п.) — приоритетнее cookie при реги. */
export function extractRefCodeFromUrl(raw: string | null | undefined): string | null {
  const trimmed = (raw || '').trim()
  if (!trimmed) return null

  const candidates = [trimmed]
  if (/%[0-9A-Fa-f]{2}/.test(trimmed)) {
    try {
      const decoded = decodeURIComponent(trimmed)
      if (decoded !== trimmed) candidates.push(decoded)
    } catch {}
  }

  for (const candidate of candidates) {
    try {
      const fromParams = new URL(candidate, window.location.origin).searchParams.get(
        'REF_CODE',
      )
      if (fromParams) return fromParams
    } catch {}
    const qIndex = candidate.indexOf('?')
    if (qIndex >= 0) {
      const fromSearch = new URLSearchParams(candidate.slice(qIndex + 1)).get(
        'REF_CODE',
      )
      if (fromSearch) return fromSearch
    }
  }

  return null
}

export function extractNestedBackUrl(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  let fromQuery: string | null = null
  try {
    fromQuery = new URL(trimmed, window.location.origin).searchParams.get(
      BACK_URL_QUERY_KEY,
    )
  } catch {}
  if (!fromQuery) {
    const qIndex = trimmed.indexOf('?')
    if (qIndex >= 0) {
      fromQuery = new URLSearchParams(trimmed.slice(qIndex + 1)).get(
        BACK_URL_QUERY_KEY,
      )
    }
  }

  const nested = decodeBackUrlValue(fromQuery || '')
  return isUsableBackUrl(nested) ? nested : null
}

export function readBackUrlFromLocationSearch(): string | null {
  try {
    const raw = new URLSearchParams(window.location.search).get(
      BACK_URL_QUERY_KEY,
    )
    return raw && raw.length ? raw : null
  } catch {
    return null
  }
}

export function resolveBackUrl(queryBackUrl?: string | null): string {
  let url = decodeBackUrlValue(queryBackUrl || '')

  const nestedFromQuery = extractNestedBackUrl(url)
  if (nestedFromQuery) url = nestedFromQuery

  if (!isUsableBackUrl(url)) {
    const stored = readSessionString(BACK_URL_SESSION_KEY, '').trim()
    if (isUsableBackUrl(stored)) url = stored
  }

  if (
    !isUsableBackUrl(url) &&
    typeof document !== 'undefined' &&
    document.referrer.includes('coral')
  ) {
    const nestedFromReferrer = extractNestedBackUrl(document.referrer)
    if (nestedFromReferrer) {
      url = nestedFromReferrer
    } else if (!isAuthSpaUrl(document.referrer)) {
      url = document.referrer
    }
  }

  if (url && isAuthSpaUrl(url)) {
    const nested = extractNestedBackUrl(url)
    if (nested) url = nested
  }

  if (isUsableBackUrl(url)) return url

  const stored = readSessionString(BACK_URL_SESSION_KEY, '').trim()
  if (isUsableBackUrl(stored)) return stored

  return '/'
}

export function getEffectiveBackUrl(storeBackUrl: string): string {
  if (isUsableBackUrl(storeBackUrl)) return storeBackUrl
  const stored = readSessionString(BACK_URL_SESSION_KEY, '').trim()
  if (isUsableBackUrl(stored)) return stored
  return '/'
}

export function resolveNavigationBackUrl(hint?: string | null): string | null {
  let back = decodeBackUrlValue(hint || '')
  if (!isUsableBackUrl(back)) {
    const stored = readSessionString(BACK_URL_SESSION_KEY, '').trim()
    if (isUsableBackUrl(stored)) back = stored
  }
  return isUsableBackUrl(back) ? back : null
}

export function resolveSuccessPageBackUrl(
  query: Record<string, unknown>,
  storeBackUrl = '/',
): string {
  const raw = String(
    query[SUCCESS_BACK_URL_QUERY_KEY] || query[BACK_URL_QUERY_KEY] || '',
  )
  const decoded = decodeBackUrlValue(raw)
  if (isUsableBackUrl(decoded)) return decoded

  const nested = extractNestedBackUrl(decoded)
  if (nested) return nested

  return getEffectiveBackUrl(storeBackUrl)
}
